import React, { SyntheticEvent, useCallback, useId, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import {
  Account,
  Participant,
  useCurrentParticipantId,
  useLogout,
} from '@app/api';
import {
  Button,
  useParticipantDisplayName,
  NotificationPin,
  SquareButton,
  useParticipantUnreadMessageByParticipation,
  MenuWindow,
  MenuItem,
  useBoolModalState,
} from '@app/core';
import { ParticipantLogo } from '../ParticipantLogo';
import { LoginParticipantModal } from '@app/auth';
import { useTranslation } from 'react-i18next';

const useStyles = createUseStyles((theme: Theme) => ({
  buttonContent: {
    display: 'flex',
    gap: theme.marginBase,
    alignItems: 'center',
  },
  button: {
    minWidth: 0,

    [theme.media.tablet]: {
      padding: 0,
    },
    [theme.media.mobile]: {
      padding: 0,
    },
  },
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.marginBase * 2,
  },
  participantName: {
    [theme.media.tablet]: {
      display: 'none',
    },
  },
  messageButton: {
    position: 'relative',
  },
}));

interface Props {
  user: Participant | Account | undefined;
  openModalMessages?: () => any;
  participationId?: string;
}

export const ProfileMenu = ({
  user,
  openModalMessages,
  participationId,
}: Props) => {
  const classes = useStyles();
  const menuId = useId();
  const [loginModalOpened, openLoginModal, closeLoginModal] =
    useBoolModalState();
  const { t } = useTranslation('common');
  const participantId = useCurrentParticipantId(true);
  const [anchorElmnt, setAnchorElmnt] = useState<null | Element>(null);

  const { mutate: logoutMut } = useLogout({ noRedirect: true });
  const logout = useCallback(() => logoutMut(), [logoutMut]);

  const onProfileClick = useCallback(
    (event: SyntheticEvent) => {
      event.stopPropagation();
      setAnchorElmnt((el) => (el ? null : event.currentTarget));
    },
    [setAnchorElmnt],
  );
  const onClose = useCallback(
    (event: SyntheticEvent) => {
      event.stopPropagation();
      setAnchorElmnt(null);
    },
    [setAnchorElmnt],
  );

  const unreadMessagesCount = useParticipantUnreadMessageByParticipation(
    participantId,
    participationId,
  );

  const displayMessages = !!openModalMessages && !!user;
  const userName = useParticipantDisplayName(user);

  return (
    <div className={classes.container}>
      {user ? (
        <>
          <Button
            variant="transparent"
            contentClassName={classes.button}
            className={classes.button}
            color="ink"
            onClick={onProfileClick}
            aria-controls={menuId}
            aria-haspopup="true"
          >
            <div className={classes.buttonContent}>
              <span className={classes.participantName}>{userName}</span>
              <ParticipantLogo user={user} small />
            </div>
          </Button>
          <MenuWindow
            open={!!anchorElmnt}
            onClose={onClose}
            id={menuId}
            anchorEl={anchorElmnt}
            onClick={onClose}
          >
            <MenuItem onClick={logout}>{t('auth.logout')}</MenuItem>
          </MenuWindow>
        </>
      ) : (
        <Button variant="transparent" small onClick={openLoginModal}>
          {t('auth.login.submit')}
        </Button>
      )}

      <LoginParticipantModal
        isOpen={loginModalOpened}
        onRequestClose={closeLoginModal}
        onSuccess={closeLoginModal}
      />

      {displayMessages && (
        <SquareButton
          icon="ri-chat-3-line"
          onClick={openModalMessages}
          color="blue"
          className={classes.messageButton}
        >
          {unreadMessagesCount > 0 && <NotificationPin />}
        </SquareButton>
      )}
    </div>
  );
};
